import styled from '@emotion/styled';
import type { InputHTMLAttributes, Ref } from 'react';
import { forwardRef } from 'react';
import { alphaHex, colors } from '@/constants/colors';

type CheckboxStyle = {
  boxBorderColor?: string,
  boxColor?: string,
  boxRadius?: number,
  boxSize?: number,
  checkedBoxColor?: string,
  checkedContentColor?: string,
  color?: string,
  disabledBoxColor?: string,
  fontSize?: number,
  fontWeight?: number,
  hoverBoxColor?: string,
  hoverBorderColor?: string,
  lineHeight?: number,
  margin?: string,
};

interface CheckboxProps extends InputHTMLAttributes<HTMLInputElement> {
  boxStyle?: CheckboxStyle;
  justBox?: boolean;
}

const defaultBoxStyle: CheckboxStyle = {
  boxBorderColor: colors.black200,
  boxColor: colors.white,
  boxRadius: 4,
  boxSize: 18,
  checkedBoxColor: colors.blue,
  checkedContentColor: colors.white,
  color: colors.black,
  disabledBoxColor: colors.grey100,
  fontSize: 15,
  fontWeight: 500,
  hoverBoxColor: 'none',
  hoverBorderColor: colors.black800,
  lineHeight: 23,
  margin: '5px 0',
};

const CheckboxLabel = styled.label<CheckboxStyle & { justBox?: boolean }>`
position: relative;
display: flex;
min-height: ${({ boxSize = defaultBoxStyle.boxSize }) => boxSize}px;
align-items: center;
padding-left: ${({ boxSize = defaultBoxStyle.boxSize, justBox }) => justBox ? (boxSize ?? 0) : (boxSize ?? 0) + 10}px;
margin: ${({ margin = defaultBoxStyle.margin }) => margin};
color: ${({ color = defaultBoxStyle.color }) => color};
cursor: pointer;
font-size: ${({ fontSize = defaultBoxStyle.fontSize }) => fontSize}px;
font-weight: ${({ fontWeight = defaultBoxStyle.fontWeight }) => fontWeight};
line-height: ${({ lineHeight = defaultBoxStyle.lineHeight }) => lineHeight}px;
user-select: none;

& > input + span:hover {
  border-color: ${({ hoverBorderColor = defaultBoxStyle.hoverBorderColor }) => hoverBorderColor};
  background-color: ${({ hoverBoxColor = defaultBoxStyle.hoverBoxColor }) => hoverBoxColor};
}

& > input:disabled + span {
  border-color: ${({ boxBorderColor = defaultBoxStyle.boxBorderColor }) => ` ${boxBorderColor}${alphaHex(0.4)}`};
  background-color: ${({ disabledBoxColor = defaultBoxStyle.disabledBoxColor }) => ` ${disabledBoxColor}${alphaHex(0.4)}`};
  cursor: not-allowed;
}

& > input:disabled:checked + span {
  background-color: ${({ checkedBoxColor = defaultBoxStyle.checkedBoxColor }) => ` ${checkedBoxColor}${alphaHex(0.4)}`};}

& > input:checked + span {
  border-width: 0;
  background-color: ${({ checkedBoxColor = defaultBoxStyle.checkedBoxColor }) => checkedBoxColor};
}

& > input:checked + span:after {
  position: relative;
  bottom: ${({ boxSize = defaultBoxStyle.boxSize }) => (boxSize ?? 0) * 0.05}px;
  width: ${({ boxSize = defaultBoxStyle.boxSize }) => (boxSize ?? 0) * 0.25}px;
  height: ${({ boxSize = defaultBoxStyle.boxSize }) => (boxSize ?? 0) * 0.5}px;
  border: solid ${({ checkedContentColor = defaultBoxStyle.checkedContentColor }) => checkedContentColor};
  border-width: 0 ${({ boxSize = defaultBoxStyle.boxSize }) => (boxSize ?? 0) * 0.09}px ${({ boxSize = defaultBoxStyle.boxSize }) => (boxSize ?? 0) * 0.09}px 0;
  content: "";
  transform: rotate(45deg);
}
`;

const CheckBoxInput = styled.input`
  display: none;
`;

const CheckboxSpan = styled.span<CheckboxStyle>`
  position: absolute;
  top: 50%;
  left: 0;
  display: flex;
  width: ${({ boxSize = defaultBoxStyle.boxSize }) => boxSize}px;
  height: ${({ boxSize = defaultBoxStyle.boxSize }) => boxSize}px;
  align-items: center;
  justify-content: center;
  border: ${({
    boxSize = defaultBoxStyle.boxSize,
    boxBorderColor = defaultBoxStyle.boxBorderColor,
  }) => ` ${(boxSize ?? 0) * 0.11}px solid ${boxBorderColor}`};
  background-color: ${({ boxColor = defaultBoxStyle.boxColor }) => boxColor};
  border-radius: ${({ boxRadius = defaultBoxStyle.boxRadius }) => boxRadius}px;
  transform: translateY(-50%);
`;

const Checkbox = ({
  boxStyle,
  justBox,
  className,
  children,
  ...property
}: CheckboxProps,
forwardedRef: Ref<HTMLInputElement>) => (
  <CheckboxLabel
    {...boxStyle}
    justBox={justBox}
    className={className}
  >
    {!justBox && children}
    <CheckBoxInput
      {...property}
      type="checkbox"
      ref={forwardedRef}
    />
    <CheckboxSpan {...boxStyle} />
  </CheckboxLabel>
);

export default forwardRef(Checkbox);
